import { useEffect, useRef } from "react";
import eleanorLogo from "src/assets/EH-Logo-default.png";
import { getGoogleClientId } from "src/env";

interface Props {
  onLogin: (token: string, email: string) => void;
}

export function SSOScreen({ onLogin }: Props) {
  const onLoginRef = useRef(onLogin);

  useEffect(() => {
    onLoginRef.current = onLogin;
  }, [onLogin]);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const clientId = getGoogleClientId();
    if (!clientId) {
      return;
    }

    const abort = new AbortController();

    function init() {
      if (abort.signal.aborted) {
        return;
      }
      if (!window.google?.accounts?.id) {
        setTimeout(init, 50);
        return;
      }

      window.google.accounts.id.initialize({
        client_id: clientId!,
        callback: ({ credential }) => {
          const email = decodeEmail(credential);
          if (email) onLoginRef.current(credential, email);
        },
        auto_select: true,
      });

      window.google.accounts.id.prompt((notification) => {
        if (
          (notification.isNotDisplayed() || notification.isSkippedMoment()) &&
          buttonRef.current
        ) {
          window.google!.accounts.id.renderButton(buttonRef.current, {
            type: "standard",
            theme: "outline",
            size: "large",
            text: "signin_with",
          });
        }
      });
    }

    init();
    return () => {
      abort.abort();
    };
  }, []);

  return (
    <div className="login-gate">
      <div className="login-card">
        <div className="brand">
          <img src={eleanorLogo} alt="Eleanor Health" className="brand-logo" />
          <span className="brand-name">Eleanor Health</span>
        </div>
        <h1>Walk-in dashboard</h1>
        <p className="login-subtitle">
          Sign in with your Eleanor Health Google Account. Automatic SSO login is enabled. The
          button will be displayed only if necessary.
        </p>
        <div ref={buttonRef} className="sso-button-wrap" />
      </div>
    </div>
  );
}

function decodeEmail(token: string): string {
  try {
    const payload = token.split(".")[1];
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded)) as { email?: string };
    return decoded.email ?? "";
  } catch {
    return "";
  }
}
